/**
 * Product Configuration Service
 * 
 * PHASE 1.3: Product Mapping & Configuration
 * 
 * This service provides centralized product configuration that maps Stripe products
 * to App Store Connect product IDs, ensuring pricing parity and supporting
 * environment-specific configurations for sandbox vs production environments.
 * 
 * FEATURES:
 * - Centralized product mapping between Stripe and iOS IAP
 * - Pricing parity validation to ensure consistent pricing across platforms
 * - Environment-specific configurations (sandbox/production)
 * - Future-ready structure for Android product mapping
 * - Type-safe product definitions with comprehensive validation
 * 
 * INTEGRATION:
 * - Used by IOSInAppPurchaseService for product registration
 * - Used by PaymentRoutingService for product ID mapping
 * - Integrates with existing pricing configuration from subscription system
 * - Supports existing Stripe price ID environment variables
 */

import { pricingConfig } from '@/hooks/subscription/types';

// Environment types
export type Environment = 'sandbox' | 'production';

// Platform types for future expansion
export type Platform = 'ios' | 'android' | 'stripe';

// Product types matching cordova-plugin-purchase
export type ProductType = 'paid subscription' | 'consumable' | 'non consumable';

// Subscription intervals
export type SubscriptionInterval = 'monthly' | 'annual';

/**
 * Base product configuration interface
 */
export interface BaseProductConfig {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly interval: SubscriptionInterval;
  readonly priceInCents: number;
  readonly formattedPrice: string;
  readonly currency: string;
}

/**
 * Stripe-specific product configuration
 */
export interface StripeProductConfig extends BaseProductConfig {
  readonly platform: 'stripe';
  readonly priceId: string; // Stripe price ID from environment variables
}

/**
 * iOS IAP-specific product configuration
 */
export interface IOSProductConfig extends BaseProductConfig {
  readonly platform: 'ios';
  readonly productId: string; // App Store Connect product ID
  readonly type: ProductType;
}

/**
 * Android IAP-specific product configuration (future Phase 6)
 */
export interface AndroidProductConfig extends BaseProductConfig {
  readonly platform: 'android';
  readonly productId: string; // Google Play product ID
  readonly type: ProductType;
}

/**
 * Unified product configuration supporting all platforms
 */
export interface ProductConfiguration {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly interval: SubscriptionInterval;
  readonly priceInCents: number;
  readonly formattedPrice: string;
  readonly currency: string;
  readonly stripe: StripeProductConfig;
  readonly ios: IOSProductConfig;
  readonly android?: AndroidProductConfig; // Optional for Phase 6
}

/**
 * Environment-specific configuration
 */
export interface EnvironmentConfig {
  readonly environment: Environment;
  readonly iosProductIdPrefix: string;
  readonly androidProductIdPrefix?: string; // Optional for Phase 6
  readonly validationEndpoint?: string; // For receipt validation
}

/**
 * Product mapping validation result
 */
export interface ValidationResult {
  readonly isValid: boolean;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

/**
 * Product Configuration Service
 * Manages centralized product configuration and mapping between platforms
 */
export class ProductConfigurationService {
  private readonly environment: Environment;
  private readonly environmentConfig: EnvironmentConfig;
  private readonly products: Map<string, ProductConfiguration> = new Map();

  constructor(environment: Environment = 'production') {
    this.environment = environment;
    this.environmentConfig = this.getEnvironmentConfig(environment);
    
    console.log(`🔧 Product Configuration: Initializing for ${environment} environment`);
    
    // Initialize product configurations
    this.initializeProducts();
    
    // Validate configurations
    const validation = this.validateConfiguration();
    if (!validation.isValid) {
      console.error('❌ Product Configuration: Validation failed:', validation.errors);
      throw new Error(`Product configuration validation failed: ${validation.errors.join(', ')}`);
    }
    
    if (validation.warnings.length > 0) {
      console.warn('⚠️ Product Configuration: Warnings:', validation.warnings);
    }
    
    console.log(`✅ Product Configuration: Initialized with ${this.products.size} products`);
  }

  /**
   * Get environment-specific configuration
   */
  private getEnvironmentConfig(environment: Environment): EnvironmentConfig {
    const configs: Record<Environment, EnvironmentConfig> = {
      sandbox: {
        environment: 'sandbox',
        iosProductIdPrefix: 'com.teamjumo.thematmonster.premium',
        androidProductIdPrefix: 'com.teamjumo.thematmonster.premium', // Future Phase 6
        validationEndpoint: 'https://sandbox-itunes.apple.com/verifyReceipt'
      },
      production: {
        environment: 'production',
        iosProductIdPrefix: 'com.teamjumo.thematmonster.premium',
        androidProductIdPrefix: 'com.teamjumo.thematmonster.premium', // Future Phase 6
        validationEndpoint: 'https://buy.itunes.apple.com/verifyReceipt'
      }
    };

    return configs[environment];
  }

  /**
   * Initialize product configurations using existing pricing data
   */
  private initializeProducts(): void {
    // Monthly subscription product
    const monthlyProduct: ProductConfiguration = {
      id: 'premium_monthly',
      name: 'The Mat Monster Premium Monthly',
      description: 'Monthly subscription to The Mat Monster premium features',
      interval: 'monthly',
      priceInCents: pricingConfig.monthly.amount,
      formattedPrice: pricingConfig.monthly.formattedPrice,
      currency: 'USD',
      stripe: {
        id: 'premium_monthly_stripe',
        name: 'The Mat Monster Premium Monthly (Stripe)',
        description: 'Monthly subscription via Stripe',
        interval: 'monthly',
        priceInCents: pricingConfig.monthly.amount,
        formattedPrice: pricingConfig.monthly.formattedPrice,
        currency: 'USD',
        platform: 'stripe',
        priceId: import.meta.env.VITE_STRIPE_PRICE_ID || 'stripe_monthly_price_id_not_set'
      },
      ios: {
        id: 'premium_monthly_ios',
        name: 'The Mat Monster Premium Monthly (iOS)',
        description: 'Monthly subscription via App Store',
        interval: 'monthly',
        priceInCents: pricingConfig.monthly.amount,
        formattedPrice: pricingConfig.monthly.formattedPrice,
        currency: 'USD',
        platform: 'ios',
        productId: `${this.environmentConfig.iosProductIdPrefix}.monthly3`,
        type: 'paid subscription'
      }
    };

    // Annual subscription product
    const annualProduct: ProductConfiguration = {
      id: 'premium_annual',
      name: 'The Mat Monster Premium Annual',
      description: 'Annual subscription to The Mat Monster premium features',
      interval: 'annual',
      priceInCents: pricingConfig.annual.amount,
      formattedPrice: pricingConfig.annual.formattedPrice,
      currency: 'USD',
      stripe: {
        id: 'premium_annual_stripe',
        name: 'The Mat Monster Premium Annual (Stripe)',
        description: 'Annual subscription via Stripe',
        interval: 'annual',
        priceInCents: pricingConfig.annual.amount,
        formattedPrice: pricingConfig.annual.formattedPrice,
        currency: 'USD',
        platform: 'stripe',
        priceId: import.meta.env.VITE_STRIPE_ANNUAL_PRICE_ID || 'stripe_annual_price_id_not_set'
      },
      ios: {
        id: 'premium_annual_ios',
        name: 'The Mat Monster Premium Annual (iOS)',
        description: 'Annual subscription via App Store',
        interval: 'annual',
        priceInCents: pricingConfig.annual.amount,
        formattedPrice: pricingConfig.annual.formattedPrice,
        currency: 'USD',
        platform: 'ios',
        productId: `${this.environmentConfig.iosProductIdPrefix}.yearly3`,
        type: 'paid subscription'
      }
    };

    // Store products
    this.products.set(monthlyProduct.id, monthlyProduct);
    this.products.set(annualProduct.id, annualProduct);

    console.log('📦 Product Configuration: Initialized products:', {
      monthly: monthlyProduct.ios.productId,
      annual: annualProduct.ios.productId,
      environment: this.environment
    });
  }

  /**
   * Validate product configuration for consistency and completeness
   */
  private validateConfiguration(): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate each product
    for (const [productId, product] of this.products) {
      // Check pricing consistency between platforms
      if (product.stripe.priceInCents !== product.ios.priceInCents) {
        errors.push(`Pricing mismatch for ${productId}: Stripe=${product.stripe.priceInCents}, iOS=${product.ios.priceInCents}`);
      }

      // Check for missing environment variables
      if (product.stripe.priceId.includes('not_set')) {
        warnings.push(`Stripe price ID not configured for ${productId}`);
      }

      // Validate iOS product ID format
      if (!product.ios.productId.startsWith(this.environmentConfig.iosProductIdPrefix)) {
        errors.push(`iOS product ID format invalid for ${productId}: ${product.ios.productId}`);
      }
    }

    // Check minimum required products
    if (this.products.size < 2) {
      errors.push('Minimum 2 products required (monthly and annual)');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Get all product configurations
   */
  getAllProducts(): readonly ProductConfiguration[] {
    return Array.from(this.products.values());
  }

  /**
   * Get product configuration by ID
   */
  getProduct(productId: string): ProductConfiguration | undefined {
    return this.products.get(productId);
  }

  /**
   * Get product configuration by subscription interval
   */
  getProductByInterval(interval: SubscriptionInterval): ProductConfiguration | undefined {
    return Array.from(this.products.values()).find(product => product.interval === interval);
  }

  /**
   * Get iOS product configurations for cordova-plugin-purchase registration
   */
  getIOSProducts(): readonly IOSProductConfig[] {
    return Array.from(this.products.values()).map(product => product.ios);
  }

  /**
   * Get Stripe product configurations
   */
  getStripeProducts(): readonly StripeProductConfig[] {
    return Array.from(this.products.values()).map(product => product.stripe);
  }

  /**
   * Map subscription interval to iOS product ID
   */
  getIOSProductId(interval: SubscriptionInterval): string | undefined {
    const product = this.getProductByInterval(interval);
    return product?.ios.productId;
  }

  /**
   * Map subscription interval to Stripe price ID
   */
  getStripePriceId(interval: SubscriptionInterval): string | undefined {
    const product = this.getProductByInterval(interval);
    return product?.stripe.priceId;
  }

  /**
   * Get current environment configuration
   */
  getCurrentEnvironmentConfig(): EnvironmentConfig {
    return this.environmentConfig;
  }

  /**
   * Get current environment
   */
  getCurrentEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Get platform information for debugging
   */
  getDebugInfo(): Record<string, unknown> {
    return {
      environment: this.environment,
      environmentConfig: this.environmentConfig,
      productsCount: this.products.size,
      products: Array.from(this.products.entries()).map(([id, product]) => ({
        id,
        name: product.name,
        interval: product.interval,
        priceInCents: product.priceInCents,
        iosProductId: product.ios.productId,
        stripeProductId: product.stripe.priceId
      })),
      validation: this.validateConfiguration()
    };
  }
}

// Export singleton instance with environment detection
const getEnvironment = (): Environment => {
  // In development/testing, use sandbox
  if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') {
    return 'sandbox';
  }

  // Check for explicit environment variable
  const envVar = import.meta.env.VITE_IAP_ENVIRONMENT as Environment;
  if (envVar === 'sandbox' || envVar === 'production') {
    return envVar;
  }
  
  // Default to production for safety
  return 'production';
};

export const productConfigurationService = new ProductConfigurationService(getEnvironment());
